import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, ExternalLink, MessagesSquare } from "lucide-react";
import { CHAT_APP_PATH, chatAppUrl } from "../../services/chatApp";
import { isIOS } from "../../services/pwa";

// Shown instead of the ERP when it's opened as the old installed ERP app, which is discontinued:
// chat now has its own app (IRG Chat) and the ERP is used in the browser.
function LegacyAppNotice() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const android = /Android/.test(window.navigator.userAgent);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(chatAppUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the link is shown on screen to copy by hand */
    }
  };

  const removeHint = isIOS()
    ? "Touch and hold this app's icon on your Home Screen, then tap Remove App."
    : android
      ? "Touch and hold this app's icon, then tap Uninstall."
      : "Open this window's ⋮ menu and choose Uninstall.";

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-white px-5 py-[max(2rem,env(safe-area-inset-top))] dark:bg-gray-900">
      <div className="w-full max-w-md text-center">
        <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white">
          <MessagesSquare size={28} aria-hidden="true" />
        </span>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">This app has been replaced by IRG Chat</h1>
        <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
          Chat now has its own app. Use the ERP in your browser at <b>{window.location.host}</b>.
        </p>

        <div className="mt-6 space-y-2.5">
          <button
            type="button"
            onClick={() => navigate(CHAT_APP_PATH)}
            className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 active:bg-blue-700"
          >
            Go to chat
          </button>
          <a
            href={`${window.location.origin}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <ExternalLink size={16} aria-hidden="true" /> Open the ERP in the browser
          </a>
        </div>

        <div className="mt-8 rounded-2xl bg-gray-50 p-4 text-left text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          <p className="font-semibold text-gray-900 dark:text-gray-100">Switch to the IRG Chat app</p>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5">
            <li>
              In {isIOS() ? "Safari" : "Chrome or Edge"}, open{" "}
              <button type="button" onClick={copyLink} className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400">
                {chatAppUrl()}
                {copied ? <Check size={14} aria-label="Copied" /> : <Copy size={14} aria-label="Copy link" />}
              </button>{" "}
              and install IRG Chat from there.
            </li>
            <li>Remove this old app. {removeHint}</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default LegacyAppNotice;
